import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Copy, Check, Gift, Loader2, AlertCircle, ChevronDown, Clock } from 'lucide-react';
import {
  initScratchCard, prefersReducedMotion, readStoredDraw, storeDraw,
  readScratchSession, storeScratchSession,
} from '../utils/scratchCard';
import {
  trackVipScratchStarted, trackVipPrizeRevealed, trackVipSubscribed,
  trackNewsletterSignup, trackVipPopup,
} from '../utils/analytics';

/**
 * VIP kaparós sorsjegy – állapotgép.
 *
 *   scratch → drawing → reveal → capture → code
 *
 * HÁROM SZABÁLY, amit ez a komponens betart:
 *
 * 1. A NYEREMÉNY A SZERVEREN DŐL EL. A `prize` state csak a `/api/vip/scratch`
 *    válaszából kaphat értéket – a komponensben NINCS nyereménylista, és a
 *    kaparás megkezdése előtt a DOM-ban sem szerepel egyetlen nyeremény sem.
 * 2. A NYERÉSI ESÉLYEK NYILVÁNOSAK. A reveal képernyőn ott a „Nyerési esélyek”
 *    panel (a szerver `/api/vip/odds`-ából) és a játékszabályzat linkje.
 * 3. AZ ABLAK VÉGIG BEZÁRHATÓ. Az X minden állapotban látszik, a capture
 *    képernyőn „Most nem kérem” link is van, és a nyereményt a látogató az
 *    e-mail megadása ELŐTT látja.
 *
 * Nincs visszaszámláló, nincs sürgetés, telefonszámot sehol nem kérünk.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/;

/** `?source=` – a kód-prefixhez. Ismeretlen/hiányzó → a szerver WEB-re esik. */
const readSource = () => {
  try {
    return new URLSearchParams(window.location.search).get('source') || '';
  } catch {
    return '';
  }
};

// ── Nyerési esélyek panel ────────────────────────────────────────────────────
function OddsPanel({ open, onToggle }) {
  const [odds, setOdds] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || odds || failed) return;
    let alive = true;
    fetch(`${API_BASE}/api/vip/odds`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('odds'))))
      .then((d) => { if (alive) setOdds(d); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [open, odds, failed]);

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid="vip-odds-toggle"
        className="inline-flex items-center gap-1 text-xs text-gray-500 underline underline-offset-4 hover:text-ink min-h-[44px]"
      >
        Nyerési esélyek
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3" data-testid="vip-odds-panel">
          {failed && <p className="text-xs text-gray-500">Az esélytábla most nem érhető el. Megnézheted a <a href="/vip-jatekszabalyzat/" className="underline">játékszabályzatban</a>.</p>}
          {!odds && !failed && <p className="text-xs text-gray-500">Töltés…</p>}
          {odds && (
            <>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 text-left">
                    <th className="font-medium pb-1">Nyeremény</th>
                    <th className="font-medium pb-1 text-right">Esély</th>
                  </tr>
                </thead>
                <tbody>
                  {odds.prizes.map((p) => (
                    <tr key={p.code} className="border-t border-gray-200">
                      <td className="py-1 pr-2 text-ink">{p.label}</td>
                      <td className="py-1 text-right tabular-nums font-semibold text-ink">{p.chance_percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">{odds.common_terms}</p>
            </>
          )}
          <a href="/vip-jatekszabalyzat/" className="inline-block mt-2 text-xs text-brand-hover underline underline-offset-2">
            Teljes játékszabályzat
          </a>
        </div>
      )}
    </div>
  );
}

export default function VipScratch({ onClose, variant = 'inline', className = '' }) {
  // 'scratch' | 'drawing' | 'reveal' | 'capture' | 'code' | 'cooldown'
  const [phase, setPhase] = useState('scratch');
  const [prize, setPrize] = useState(null);       // CSAK a szerver válaszából
  const [drawToken, setDrawToken] = useState('');
  const [drawError, setDrawError] = useState('');
  const [oddsOpen, setOddsOpen] = useState(false);

  const [form, setForm] = useState({ name: '', email: '' });
  const [consent, setConsent] = useState(false);
  const [hp, setHp] = useState('');               // honeypot
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const canvasRef = useRef(null);
  const cardRef = useRef(null);
  const rootRef = useRef(null);
  const failCountRef = useRef(0);
  const drawInFlightRef = useRef(false);
  const source = useRef(readSource()).current;
  const reduced = useRef(prefersReducedMotion()).current;

  // Fázisváltáskor a fókuszban lévő gomb unmountol (scratch→reveal→capture→code),
  // a fókusz a body-ra esne – modálban (EmailPopup) a Tab innen a háttérre
  // szökne. A váltás után ezért az új nézet elejére állunk: az első
  // fókuszolható elemre. CSAK akkor nyúlunk a fókuszhoz, ha az tényleg
  // elveszett (nem a komponensen belül van) – inline változatban görgetést
  // se okozzunk feleslegesen.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || phase === 'scratch') return;
    // Fázisonként ÉRTELMES cél: reveal → „Kérem a kuponom”, capture → Név mező.
    // Ezekre AKKOR IS ráállunk, ha a fókusz épp a fázisokon átívelő elemen
    // (pl. „Nyerési esélyek” toggle) ült – a felfedés/űrlapnyitás tudatos
    // lépésváltás, az új nézet elején kell folytatódnia.
    const prefer =
      phase === 'reveal'  ? node.querySelector('[data-testid="vip-to-capture"]') :
      phase === 'capture' ? node.querySelector('#vs-name') : null;
    const active = document.activeElement;
    if (!prefer && active && active !== document.body && node.contains(active)) return;
    // Fallback: az első LÁTHATÓ, tabolható elem – a honeypot (tabindex=-1,
    // képernyőn kívül) és minden rejtett elem kizárva.
    const fallback = Array.from(node.querySelectorAll('button, [href], input, select, textarea'))
      .find(el => el.tabIndex >= 0 && el.getClientRects().length > 0);
    const target = prefer || fallback;
    if (target) target.focus({ preventScroll: variant === 'inline' });
  }, [phase, variant]);

  // Már nyert ezen az eszközön? Akkor a kódját mutatjuk, nem sorsolunk újra.
  useEffect(() => {
    const stored = readStoredDraw();
    if (stored && stored.code) {
      setResult({
        code: stored.code,
        prize_label: stored.prize_label,
        condition_text: stored.condition_text,
        common_terms: stored.common_terms,
        expires_at: stored.expires_at,
        already_member: true,
      });
      setPrize({ prize: stored.prize, prize_label: stored.prize_label, condition_text: stored.condition_text });
      setPhase('code');
    }
  }, []);

  // ── A sorsolás: EZ a szerverhívás ─────────────────────────────────────────
  const runDraw = useCallback(async () => {
    // Dupla kattintás / lassú hálózat: amíg egy sorsolás úton van, nem indul
    // második. (A szerver a munkamenet-index miatt akkor sem sorsolna kétszer,
    // ez csak a felesleges kérést spórolja meg.)
    if (drawInFlightRef.current) return;
    drawInFlightRef.current = true;
    setPhase('drawing');
    setDrawError('');
    trackVipScratchStarted(source);
    try {
      const res = await fetch(`${API_BASE}/api/vip/scratch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // `session`: a korábbi scratch-válasz aláírt munkamenet-tokenje. Amíg
        // ehhez élő jegy tartozik, a szerver ugyanazt a nyereményt adja vissza
        // (reroll-védelem) – refresh és új fül sem sorsol újra.
        body: JSON.stringify({ source, website: hp, session: readScratchSession() || undefined }),
      });
      // A 429 NEM hiba, hanem korlát – külön üzenetet érdemel, különben a
      // látogató azt hiszi, elromlott valami, és újra meg újra próbálkozik.
      if (res.status === 429) {
        const d = await res.json().catch(() => ({}));
        setDrawError(d.detail || 'Túl sok próbálkozás. Nézz vissza kicsit később.');
        setPhase('scratch');
        trackVipPopup('error', 'scratch_rate_limited');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.prize) throw new Error('empty');
      failCountRef.current = 0;
      storeScratchSession(data.session);
      setPrize(data);
      setDrawToken(data.draw_token);
      setPhase('reveal');
      // Ismételt kérés (refresh, másik fül): ugyanaz a jegy jött vissza – a
      // mérésben nem számít új reveal-nek.
      if (!data.repeat) trackVipPrizeRevealed(data.prize, source);
      try { cardRef.current?.clearAll(); } catch { /* dísz */ }
    } catch (e) {
      failCountRef.current += 1;
      setDrawError(
        failCountRef.current >= 2
          ? 'A sorsolás most nem érhető el. Próbáld meg később, vagy szólj nekünk az üzletben.'
          : 'Nem sikerült a sorsolás, próbáld újra.'
      );
      setPhase('scratch');
      trackVipPopup('error', 'scratch_failed');
    } finally {
      drawInFlightRef.current = false;
    }
  }, [source, hp]);

  // ── Canvas ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'scratch' || reduced) return undefined;
    const card = initScratchCard(canvasRef.current, { onThreshold: runDraw, threshold: 0.4 });
    // Ha a canvas nem hozható létre (régi böngésző, kikapcsolt canvas), a
    // „Felfedem” gomb marad – a feliratkozás sosem akadhat el emiatt.
    cardRef.current = card;
    return () => { try { card?.destroy(); } catch { /* nem kritikus */ } };
  }, [phase, reduced, runDraw]);

  // ── Feliratkozás ──────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Kérlek add meg a neved.';
    if (!form.email.trim()) e.email = 'Kérlek add meg az e-mail címed.';
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'Ez az e-mail cím nem tűnik érvényesnek.';
    if (!consent) e.consent = 'A kuponhoz el kell fogadnod az adatkezelési tájékoztatót.';
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    const fe = validate();
    setFieldErrors(fe);
    if (Object.keys(fe).length) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch(`${API_BASE}/api/vip/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draw_token: drawToken,
          name: form.name.trim(),
          email: form.email.trim(),
          consent: true,
          website: hp,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError(data.detail || 'Nem sikerült elmenteni. Próbáld újra.');
        setSubmitting(false);
        return;
      }
      // Már játszott az elmúlt 30 napban, és a kuponját beváltotta: nincs új
      // jegy, és kódot sem adunk vissza. Saját képernyőt kap – nem hibaüzenetet,
      // mert nem hibázott.
      if (data.already_played) {
        setResult(data);
        setPhase('cooldown');
        trackVipSubscribed('none', source, true);
        setSubmitting(false);
        return;
      }
      setResult(data);
      setPhase('code');
      storeDraw({
        code: data.code,
        prize: data.prize,
        prize_label: data.prize_label,
        condition_text: data.condition_text,
        common_terms: data.common_terms,
        expires_at: data.expires_at,
      });
      // A Google Ads / Meta konverzió a MEGLÉVŐ esemény marad – a historikus
      // adat nem szakadhat meg. Ismételt beküldésnél nem tüzel újra.
      if (!data.repeat && !data.already_member) trackNewsletterSignup('vip_scratch');
      trackVipSubscribed(data.prize, source, !!(data.repeat || data.already_member));
    } catch {
      setSubmitError('Hálózati hiba. Ellenőrizd a kapcsolatot és próbáld újra.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = () => {
    if (!result?.code) return;
    navigator.clipboard?.writeText(result.code)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => { /* kijelölhető marad */ });
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
      if (fieldErrors[key]) setFieldErrors((x) => ({ ...x, [key]: undefined }));
    },
    'aria-invalid': fieldErrors[key] ? 'true' : undefined,
    'aria-describedby': fieldErrors[key] ? `vs-err-${key}` : undefined,
    className: `w-full border-2 rounded-xl px-4 py-3 text-base outline-none transition-colors bg-white ${
      fieldErrors[key] ? 'border-red-400' : 'border-gray-200 focus:border-brand'
    }`,
  });

  const Err = ({ k }) => (fieldErrors[k] ? (
    <p id={`vs-err-${k}`} role="alert" className="text-xs text-red-600 mt-1 flex items-center gap-1">
      <AlertCircle size={13} aria-hidden="true" /> {fieldErrors[k]}
    </p>
  ) : null);

  // ── Nézetek ───────────────────────────────────────────────────────────────
  const closeBtn = onClose ? (
    // Az X MINDEN állapotban látszik – ez a harmadik szabály.
    <button
      type="button"
      onClick={onClose}
      aria-label="Bezárás"
      data-testid="vip-scratch-close"
      className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
    >
      <X size={18} className="text-gray-600" />
    </button>
  ) : null;

  const prizeBox = prize ? (
    <div className="rounded-xl bg-brand/5 border border-brand/20 p-4 text-center" data-testid="vip-prize-box">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-hover mb-1">A nyereményed</p>
      <p className="text-2xl font-bold text-ink leading-tight" data-testid="vip-prize-label">{prize.prize_label}</p>
      <p className="text-sm text-gray-600 mt-2">{prize.condition_text}</p>
      <p className="text-xs text-gray-500 mt-1">{prize.expires_days ?? 30} napig érvényes</p>
    </div>
  ) : null;

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid="vip-scratch" data-phase={phase}>
      {closeBtn}

      {/* Honeypot – ember sosem tölti ki. */}
      <input
        type="text" name="website" value={hp} tabIndex={-1} autoComplete="off" aria-hidden="true"
        onChange={(e) => setHp(e.target.value)}
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      />

      {/* ── scratch / drawing ── */}
      {(phase === 'scratch' || phase === 'drawing') && (
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand/10 rounded-full mb-3">
            <Gift size={16} className="text-brand-hover" aria-hidden="true" />
            <span className="text-brand-hover font-semibold text-xs">VIP Klub</span>
          </div>
          <h2 className="text-2xl font-bold text-ink mb-1">Kaparj le és nézd meg, mit nyertél</h2>
          <p className="text-gray-500 text-sm mb-5">Minden jegy nyer.</p>

          <div className="relative mx-auto max-w-sm">
            {/* A takaró ALATT SZÁNDÉKOSAN nincs nyeremény-szöveg: a nyeremény a
                szerveren dől el, és csak a válasz után kerül a DOM-ba. */}
            <div className="rounded-2xl bg-gray-100 border-2 border-dashed border-gray-300 h-40 flex items-center justify-center">
              {phase === 'drawing' ? (
                <span className="inline-flex items-center gap-2 text-gray-500 text-sm">
                  <Loader2 size={18} className="animate-spin" aria-hidden="true" /> Sorsolás…
                </span>
              ) : (
                <span className="text-gray-500 text-sm">Húzd végig az ujjad</span>
              )}
            </div>

            {phase === 'scratch' && !reduced && (
              <button
                type="button"
                onClick={() => cardRef.current?.forceReveal?.() ?? runDraw()}
                aria-label="Kaparós sorsjegy — nyomj Entert a felfedéshez"
                data-testid="vip-scratch-card"
                className="absolute inset-0 rounded-2xl overflow-hidden cursor-grab active:cursor-grabbing focus:outline-none focus:ring-4 focus:ring-brand/30"
              >
                <canvas
                  ref={canvasRef}
                  className="w-full h-40 block"
                  style={{ touchAction: 'none' }}
                  aria-hidden="true"
                />
              </button>
            )}
          </div>

          {/* Fallback: reduced-motion, canvas-hiba, vagy egyszerűen aki nem akar
              kaparni. UGYANAZ a sorsolás, csak kaparás nélkül. */}
          {phase === 'scratch' && (
            <button
              type="button"
              onClick={runDraw}
              data-testid="vip-reveal-fallback"
              className="mt-4 inline-flex items-center justify-center min-h-[48px] px-6 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors"
            >
              {reduced ? 'Felfedem' : 'Inkább csak fedd fel'}
            </button>
          )}

          {drawError && (
            <div className="mt-3">
              <p role="alert" className="text-sm text-red-600">{drawError}</p>
              <button
                type="button" onClick={runDraw} data-testid="vip-draw-retry"
                className="mt-2 min-h-[44px] px-4 text-sm font-semibold text-brand-hover underline underline-offset-4"
              >
                Újrapróbálom
              </button>
            </div>
          )}

          <OddsPanel open={oddsOpen} onToggle={() => setOddsOpen((o) => !o)} />
        </div>
      )}

      {/* ── reveal ── */}
      {phase === 'reveal' && (
        <div className="text-center">
          {/* role="status": a nyeremény felolvasóval is bejelentődik, nem csak
              vizuálisan jelenik meg. */}
          <div role="status">
            <p className="text-sm font-semibold text-brand-hover mb-2">Nyertél! 🎉</p>
            {prizeBox}
          </div>
          <button
            type="button"
            onClick={() => setPhase('capture')}
            data-testid="vip-to-capture"
            className="mt-5 w-full min-h-[52px] rounded-xl bg-brand text-white font-bold text-lg hover:bg-brand-hover transition-colors"
          >
            Kérem a kuponom
          </button>
          <OddsPanel open={oddsOpen} onToggle={() => setOddsOpen((o) => !o)} />
        </div>
      )}

      {/* ── capture ── */}
      {phase === 'capture' && (
        <div>
          {/* A nyeremény ITT IS végig látszik – nem tűnik el az űrlap mögött. */}
          {prizeBox}
          <form onSubmit={submit} noValidate className="space-y-4 mt-4">
            <div>
              <label htmlFor="vs-name" className="block text-sm font-semibold text-gray-700 mb-1.5">Név</label>
              <input id="vs-name" type="text" required maxLength={120} autoComplete="name"
                placeholder="Kovács Anna" data-testid="vip-scratch-name" {...field('name')} />
              <Err k="name" />
            </div>
            <div>
              <label htmlFor="vs-email" className="block text-sm font-semibold text-gray-700 mb-1.5">E-mail cím</label>
              <input id="vs-email" type="email" required maxLength={200} autoComplete="email" inputMode="email"
                placeholder="anna@example.hu" data-testid="vip-scratch-email" {...field('email')} />
              <Err k="email" />
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer text-xs text-gray-600 leading-relaxed">
              <input
                type="checkbox" checked={consent}
                onChange={(e) => { setConsent(e.target.checked); if (fieldErrors.consent) setFieldErrors((x) => ({ ...x, consent: undefined })); }}
                className="accent-brand mt-0.5 w-5 h-5 flex-shrink-0"
                aria-invalid={fieldErrors.consent ? 'true' : undefined}
                aria-describedby={fieldErrors.consent ? 'vs-err-consent' : undefined}
                data-testid="vip-scratch-consent"
              />
              <span>
                Hozzájárulok, hogy a NorbiPhone az adataimat kupon és marketing célból kezelje.{' '}
                <a href="/adatkezeles/" target="_blank" rel="noopener noreferrer" className="text-brand-hover underline">Adatkezelési tájékoztató</a>.
              </span>
            </label>
            <Err k="consent" />

            {submitError && <p role="alert" className="text-sm text-red-600">{submitError}</p>}

            <button
              type="submit" disabled={submitting} data-testid="vip-scratch-submit"
              className="w-full min-h-[52px] rounded-xl bg-brand text-white font-bold text-lg hover:bg-brand-hover disabled:opacity-60 transition-colors"
            >
              {submitting ? 'Feldolgozás…' : 'Kérem a kuponom'}
            </button>

            {/* „Most nem kérem” – a harmadik szabály: mindig van kiút. */}
            {onClose && (
              <button
                type="button" onClick={onClose} data-testid="vip-scratch-decline"
                className="w-full min-h-[44px] text-sm text-gray-500 underline underline-offset-4 hover:text-ink"
              >
                Most nem kérem
              </button>
            )}
          </form>
        </div>
      )}

      {/* ── cooldown: már játszott, a kuponját beváltotta ── */}
      {phase === 'cooldown' && result && (
        <div className="text-center" role="status" data-testid="vip-scratch-cooldown">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <Clock size={28} className="text-gray-500" aria-hidden="true" />
          </div>
          <h2 className="text-xl font-bold text-ink mb-2">Most nem jár új jegy</h2>
          <p className="text-sm text-gray-600 leading-relaxed">{result.message}</p>
          <p className="text-sm text-gray-600 leading-relaxed mt-3">
            Addig is: ha kérdésed van egy telefonnal, gyere be bármelyik
            üzletünkbe — a hírlevelünkre továbbra is számíthatsz.
          </p>
          <a href="/vip-jatekszabalyzat/" className="inline-block mt-4 text-xs text-brand-hover underline underline-offset-2">
            Játékszabályzat
          </a>
          {onClose && (
            <button
              type="button" onClick={onClose} data-testid="vip-cooldown-close"
              className="mt-4 w-full min-h-[44px] text-sm text-gray-500 underline underline-offset-4 hover:text-ink"
            >
              Bezárom
            </button>
          )}
        </div>
      )}

      {/* ── code ── */}
      {phase === 'code' && result && (
        <div className="text-center" role="status" data-testid="vip-scratch-code">
          <div className="w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-3">
            <Check size={30} className="text-brand-hover" aria-hidden="true" />
          </div>
          <h2 className="text-xl font-bold text-ink mb-1">
            {result.already_member ? 'Már VIP tag vagy' : 'Megvan a kuponod!'}
          </h2>
          {result.already_member && (
            <p className="text-sm text-gray-600 mb-3">{result.message || 'Itt a korábbi kuponod.'}</p>
          )}
          <p className="text-lg font-semibold text-ink mt-2">{result.prize_label}</p>
          <p className="text-sm text-gray-600 mb-4">{result.condition_text}</p>

          <div className="rounded-xl border-2 border-dashed border-brand/40 bg-brand/5 p-4">
            <p className="font-mono font-bold text-2xl tracking-wider text-ink break-all" data-testid="vip-code-value">
              {result.code}
            </p>
          </div>
          <button
            type="button" onClick={copyCode} data-testid="vip-code-copy"
            className="mt-3 inline-flex items-center gap-2 min-h-[44px] px-5 rounded-xl border-2 border-gray-200 font-semibold text-ink hover:bg-gray-50 transition-colors"
          >
            {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
            {copied ? 'Kimásolva' : 'Másolás'}
          </button>

          {/* A levelet a Brevo automatizmus küldi, nem a backend – ha az a
              workflow nincs bekapcsolva, ez a mondat önmagában nem lenne igaz.
              Ezért van mellette a valódi kapaszkodó: a kód az e-mail-cím
              alapján a pultnál is előkereshető. */}
          <p className="text-sm text-gray-600 mt-4">
            E-mailben is elküldjük. Ha nem érkezne meg, az üzletben az
            e-mail-címed alapján elő tudjuk keresni.
          </p>
          {result.common_terms && (
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">{result.common_terms}</p>
          )}
          <a href="/vip-jatekszabalyzat/" className="inline-block mt-3 text-xs text-brand-hover underline underline-offset-2">
            Játékszabályzat
          </a>
        </div>
      )}

      {/* JS nélkül: a canvas nem jön létre, de a feliratkozás nem veszhet el. */}
      <noscript>
        <p className="mt-4 text-sm text-gray-600">
          A kaparós jegyhez JavaScript kell. Ha nálad ki van kapcsolva, gyere be
          bármelyik üzletünkbe, és a pultnál adunk egy kupont.
        </p>
      </noscript>
    </div>
  );
}
